import React from 'react';
import PropTypes from 'prop-types';
import {Controller} from 'react-hook-form';
import get from 'lodash/get';
import {KeyboardDatePicker, KeyboardDateTimePicker} from '@material-ui/pickers';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import {
    FORMATO_DATA_HORA_TELA,
    FORMATO_DATA_HORA_WIRE,
    FORMATO_DATA_TELA,
    FORMATO_DATA_WIRE,
    doPicker,
    paraPicker
} from './processoSelecao.js';

/*
 * Campos do formulário de Processo de Seleção (react-hook-form): texto,
 * seleção, Sim/Não e data (com ou sem hora). Todos com LabelValue,
 * FormControl outlined e o erro do campo logo abaixo, como nas etapas da
 * inscrição do administrativo. As datas ficam no form no formato do back.
 */

const mensagemErro = (errors, nome) => get(errors, nome)?.message;

export function CampoTexto({control, errors, nome, rotulo, sm = 6, multiline = false, type, maxLength, disabled = false, placeholder}) {
    const erro = mensagemErro(errors, nome);
    return (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth error={!!erro} disabled={disabled}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <OutlinedInput
                                name={field.name}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                inputRef={field.ref}
                                type={type}
                                multiline={multiline}
                                minRows={multiline ? 5 : undefined}
                                placeholder={placeholder}
                                inputProps={maxLength ? {maxLength} : undefined}/>
                        )}/>
                    {!!erro && <FormHelperText error>{erro}</FormHelperText>}
                </FormControl>
            </LabelValue>
        </Grid>
    );
}

CampoTexto.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    nome: PropTypes.string.isRequired,
    rotulo: PropTypes.string.isRequired,
    sm: PropTypes.number,
    multiline: PropTypes.bool,
    type: PropTypes.string,
    maxLength: PropTypes.number,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string
};

/* Seleção com "Selecione..." (valor ''); `opcoes` = [{valor, descricao}]. `onAlterar` recebe o novo valor depois de gravado. */
export function CampoSelect({control, errors, nome, rotulo, opcoes = [], sm = 6, disabled = false, onAlterar}) {
    const erro = mensagemErro(errors, nome);
    return (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth error={!!erro} disabled={disabled}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <Select
                                name={field.name}
                                value={field.value ?? ''}
                                onBlur={field.onBlur}
                                inputRef={field.ref}
                                displayEmpty
                                onChange={evento => {
                                    field.onChange(evento.target.value);
                                    onAlterar && onAlterar(evento.target.value);
                                }}>
                                <MenuItem value=''><em>Selecione...</em></MenuItem>
                                {opcoes.map(opcao => (
                                    <MenuItem key={opcao.valor} value={opcao.valor} title={opcao.descricao}>{opcao.descricao}</MenuItem>
                                ))}
                            </Select>
                        )}/>
                    {!!erro && <FormHelperText error>{erro}</FormHelperText>}
                </FormControl>
            </LabelValue>
        </Grid>
    );
}

CampoSelect.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    nome: PropTypes.string.isRequired,
    rotulo: PropTypes.string.isRequired,
    opcoes: PropTypes.arrayOf(PropTypes.shape({valor: PropTypes.string, descricao: PropTypes.string})),
    sm: PropTypes.number,
    disabled: PropTypes.bool,
    onAlterar: PropTypes.func
};

const SIM_NAO = [{valor: 'true', descricao: 'Sim'}, {valor: 'false', descricao: 'Não'}];

/* Rádio Sim/Não (valores 'true'/'false', '' sem escolha). */
export function CampoSimNao({control, errors, nome, rotulo, sm = 6}) {
    const erro = mensagemErro(errors, nome);
    return (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl component='fieldset' error={!!erro}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <RadioGroup row name={field.name} value={field.value ?? ''} onChange={evento => field.onChange(evento.target.value)}>
                                {SIM_NAO.map(opcao => (
                                    <FormControlLabel key={opcao.valor} value={opcao.valor} control={<Radio color='primary'/>} label={opcao.descricao}/>
                                ))}
                            </RadioGroup>
                        )}/>
                    {!!erro && <FormHelperText error>{erro}</FormHelperText>}
                </FormControl>
            </LabelValue>
        </Grid>
    );
}

CampoSimNao.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    nome: PropTypes.string.isRequired,
    rotulo: PropTypes.string.isRequired,
    sm: PropTypes.number
};

/* Data (dd/mm/aaaa) ou data e hora (dd/mm/aaaa hh:mm); precisa de um MuiPickersUtilsProvider acima. */
export function CampoData({control, errors, nome, rotulo, sm = 6, comHora = false, disableFuture = false}) {
    const erro = mensagemErro(errors, nome);
    const formatoWire = comHora ? FORMATO_DATA_HORA_WIRE : FORMATO_DATA_WIRE;
    const Picker = comHora ? KeyboardDateTimePicker : KeyboardDatePicker;
    const propsHora = comHora ? {ampm: false} : {};
    return (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <Controller
                    name={nome}
                    control={control}
                    render={({field}) => (
                        <Picker
                            {...propsHora}
                            value={paraPicker(field.value, formatoWire)}
                            onChange={(data, texto) => field.onChange(doPicker(data, texto, formatoWire))}
                            onBlur={field.onBlur}
                            format={comHora ? FORMATO_DATA_HORA_TELA : FORMATO_DATA_TELA}
                            placeholder={comHora ? 'dd/mm/aaaa hh:mm' : 'dd/mm/aaaa'}
                            mask={comHora ? '__/__/____ __:__' : '__/__/____'}
                            cancelLabel='Cancelar'
                            okLabel='OK'
                            invalidDateMessage='Data inválida'
                            maxDateMessage='Data não pode ser futura'
                            disableFuture={disableFuture}
                            inputVariant='outlined'
                            fullWidth
                            error={!!erro}
                            helperText={erro}/>
                    )}/>
            </LabelValue>
        </Grid>
    );
}

CampoData.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    nome: PropTypes.string.isRequired,
    rotulo: PropTypes.string.isRequired,
    sm: PropTypes.number,
    comHora: PropTypes.bool,
    disableFuture: PropTypes.bool
};
